// /app/frontend/src/components/compliance/map/AddNodeModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { 
  Database, Cpu, HardDrive, Globe, Users, Building2,
  Plus, X
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { nodeTypeOptions, dataCategoryOptions, legalBasisOptions } from './initialElements';

const NODE_TYPE_ICONS = {
  subjects: Users,
  source: Database,
  process: Cpu,
  store: HardDrive,
  thirdparty: Building2,
  transfer: Globe,
};

const NODE_TYPE_COLORS = {
  subjects: 'border-emerald-500 bg-emerald-500/10',
  source: 'border-blue-500 bg-blue-500/10',
  process: 'border-purple-500 bg-purple-500/10',
  store: 'border-orange-500 bg-orange-500/10',
  thirdparty: 'border-cyan-500 bg-cyan-500/10',
  transfer: 'border-rose-500 bg-rose-500/10',
};

const AddNodeModal = ({ open, onOpenChange, onAddNode, onEditNode, editingNode = null, entities = [] }) => {
  const isEditMode = !!editingNode;

  const defaultForm = {
    type: '',
    label: '',
    description: '',
    entityId: '',
    risk: 'medium',
    dataCategories: [],
    legalBasis: '',
    purpose: '',
    retention: '',
    country: '',
    hasAdequacy: false,
    safeguards: '',
    securityMeasures: [],
  };

  const [formData, setFormData] = useState(defaultForm);
  
  const [newCategory, setNewCategory] = useState('');
  const [newMeasure, setNewMeasure] = useState('');

  // Pre-fill form when editing an existing node
  useEffect(() => {
    if (open && isEditMode && editingNode?.data) {
      const d = editingNode.data;
      setFormData({
        type: d.type || '',
        label: d.label || '',
        description: d.description || '',
        entityId: d.entityId || '',
        risk: d.risk || 'medium',
        dataCategories: d.dataCategories || [],
        legalBasis: d.legalBasis || '',
        purpose: d.purpose || '',
        retention: d.retention || '',
        country: d.country || '',
        hasAdequacy: d.hasAdequacy || false,
        safeguards: d.safeguards || '',
        securityMeasures: d.securityMeasures || [],
      });
    } else if (open && !isEditMode) {
      setFormData(defaultForm);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingNode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addDataCategory = (category) => {
    if (category && !formData.dataCategories.includes(category)) {
      setFormData(prev => ({
        ...prev,
        dataCategories: [...prev.dataCategories, category]
      }));
    }
    setNewCategory('');
  };

  const removeDataCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      dataCategories: prev.dataCategories.filter(c => c !== category)
    }));
  };

  const addSecurityMeasure = (measure) => {
    if (measure && !formData.securityMeasures.includes(measure)) {
      setFormData(prev => ({
        ...prev,
        securityMeasures: [...prev.securityMeasures, measure]
      }));
    }
    setNewMeasure('');
  };

  const removeSecurityMeasure = (measure) => {
    setFormData(prev => ({
      ...prev,
      securityMeasures: prev.securityMeasures.filter(m => m !== measure)
    }));
  };

  const handleSubmit = () => {
    if (!formData.type || !formData.label) return;
    
    const nodeId = isEditMode ? editingNode.id : `node-${Date.now()}`;
    const nodeData = {
      id: nodeId,
      type: 'entityNode',
      position: isEditMode ? editingNode.position : { x: 400, y: 300 },
      data: {
        id: nodeId,
        type: formData.type,
        label: formData.label,
        description: formData.description,
        entityId: formData.entityId,
        risk: formData.risk,
        dataCategories: formData.dataCategories,
        legalBasis: formData.legalBasis,
        purpose: formData.purpose,
        retention: formData.retention,
        securityMeasures: formData.securityMeasures,
        ...(isEditMode && editingNode.data.ropaId && { ropaId: editingNode.data.ropaId }),
        ...(formData.type === 'transfer' && {
          country: formData.country,
          hasAdequacy: formData.hasAdequacy,
          safeguards: formData.safeguards,
        }),
      },
    };
    
    if (isEditMode) {
      onEditNode && onEditNode(nodeData);
    } else {
      onAddNode && onAddNode(nodeData);
    }
    onOpenChange(false);
    
    // Reset form for add mode
    if (!isEditMode) {
      setFormData(defaultForm);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-white">{isEditMode ? 'Edit Node' : 'Add Data Flow Node'}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update the details for this node.'
              : 'Create a new node to represent a data source, processing system, or transfer point.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Node Type Selection */}
          <div className="space-y-3">
            <Label>Node Type <span className="text-rose-400">*</span></Label>
            <div className="grid grid-cols-3 gap-2">
              {nodeTypeOptions.map((option) => {
                const Icon = NODE_TYPE_ICONS[option.value];
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange('type', option.value)}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      formData.type === option.value
                        ? cn(NODE_TYPE_COLORS[option.value], "ring-2 ring-offset-2 ring-offset-slate-950")
                        : "border-slate-800 hover:border-slate-700"
                    )}
                  >
                    <Icon className="h-5 w-5 mb-2 text-slate-400" />
                    <p className="text-sm font-medium text-white">{option.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name <span className="text-rose-400">*</span></Label>
              <Input
                value={formData.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="e.g., Customer Database"
                className="bg-slate-900 border-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Entity</Label>
              <Select value={formData.entityId} onValueChange={(v) => handleChange('entityId', v)}>
                <SelectTrigger className="bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.entity_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this data point..."
              className="bg-slate-900 border-slate-800"
              rows={2}
            />
          </div>

          {/* Risk Level */}
          <div className="space-y-2">
            <Label>Risk Level</Label>
            <div className="flex gap-2">
              {['low', 'medium', 'high'].map((risk) => (
                <button
                  key={risk}
                  type="button"
                  onClick={() => handleChange('risk', risk)}
                  className={cn(
                    "flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium capitalize transition-all",
                    formData.risk === risk
                      ? risk === 'low' ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                        : risk === 'medium' ? "border-amber-500 bg-amber-500/10 text-amber-400"
                        : "border-rose-500 bg-rose-500/10 text-rose-400"
                      : "border-slate-800 text-slate-400 hover:border-slate-700"
                  )}
                >
                  {risk}
                </button>
              ))}
            </div>
          </div>

          {/* Data Categories */}
          <div className="space-y-2">
            <Label>Data Categories</Label>
            <div className="flex gap-2">
              <Select value={newCategory} onValueChange={addDataCategory}>
                <SelectTrigger className="bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Add category" />
                </SelectTrigger>
                <SelectContent>
                  {dataCategoryOptions.filter(c => !formData.dataCategories.includes(c)).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.dataCategories.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.dataCategories.map((cat) => (
                  <Badge key={cat} variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/20">
                    {cat}
                    <button onClick={() => removeDataCategory(cat)} className="ml-1">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Legal Basis & Purpose */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Legal Basis</Label>
              <Select value={formData.legalBasis} onValueChange={(v) => handleChange('legalBasis', v)}>
                <SelectTrigger className="bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Select legal basis" />
                </SelectTrigger>
                <SelectContent>
                  {legalBasisOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Retention Period</Label>
              <Input
                value={formData.retention}
                onChange={(e) => handleChange('retention', e.target.value)}
                placeholder="e.g., 7 years"
                className="bg-slate-900 border-slate-800"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Purpose</Label>
            <Input
              value={formData.purpose}
              onChange={(e) => handleChange('purpose', e.target.value)}
              placeholder="Purpose of processing..."
              className="bg-slate-900 border-slate-800"
            />
          </div>

          {/* Transfer-specific fields */}
          {formData.type === 'transfer' && (
            <div className="p-4 rounded-lg border border-rose-500/20 bg-rose-500/5 space-y-4">
              <h4 className="text-sm font-semibold text-rose-400 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Cross-Border Transfer Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Destination Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="e.g., United States"
                    className="bg-slate-900 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Safeguards</Label>
                  <Input
                    value={formData.safeguards}
                    onChange={(e) => handleChange('safeguards', e.target.value)}
                    placeholder="e.g., SCCs, BCRs"
                    className="bg-slate-900 border-slate-800"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="adequacy"
                  checked={formData.hasAdequacy}
                  onCheckedChange={(v) => handleChange('hasAdequacy', v)}
                />
                <Label htmlFor="adequacy" className="text-sm cursor-pointer">
                  Adequacy decision in place
                </Label>
              </div>
            </div>
          )}

          {/* Security Measures */}
          <div className="space-y-2">
            <Label>Security Measures</Label>
            <div className="flex gap-2">
              <Input
                value={newMeasure}
                onChange={(e) => setNewMeasure(e.target.value)}
                placeholder="Add security measure..."
                className="bg-slate-900 border-slate-800"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSecurityMeasure(newMeasure))}
              />
              <Button type="button" variant="outline" onClick={() => addSecurityMeasure(newMeasure)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.securityMeasures.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.securityMeasures.map((measure) => (
                  <Badge key={measure} variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    {measure}
                    <button onClick={() => removeSecurityMeasure(measure)} className="ml-1">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.type || !formData.label}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            {isEditMode ? 'Save Changes' : 'Add Node'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNodeModal;
